import { ParsedUrlQuery } from 'querystring';

import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  useParams,
  usePathname,
  useSearchParams,
  useRouter,
} from 'next/navigation';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import moment from 'moment';

import { CloseIcon, SeturLogo } from '@assets/icons';
import {
  KidInterface,
  ListingSearchRoomInterface,
} from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import CustomIcon from '@components/utils/CustomIcon';
import { CalendarHolidaysViewModel, WebUrlType } from '@api/v3';
import { callService } from '@api/remote-call';
import { formatMomentDate, HOTEL_SEARCH_DATE_FORMAT } from '@utils/dateUtil';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import clean from '@utils/cleanObject';
import { HOTEL_PAST_SEARCH } from '@constants/constants';
import {
  hotelItemsTOStringify,
  validateGuestsChildrenAges,
  generateSearchRooms,
} from '@components/views/landing/components/booker/common/bookerUtils';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { SEARCH_LENGTH, handleFilterAndUpdateUrl } from '@utils/utils';
import { SizeUnit } from '@utils/types';
import { useDebounceValue } from '@hooks/hooks';
import MobileSearchLocation from '@components/views/hotel/components/SearchBar/components/SearchLocation/MobileSearchLocation';
import { paramsToObject } from '@components/views/utils';
import useRouterNext from '@hooks/useRouterNext';
import { generateUUID } from '@utils/generateUUID';
import {
  LANDING_BREAKPOINT,
  LARGE_SCREEN_BREAKPOINT,
} from '@utils/theme/breakPoint';

import ResponsiveListingSearchGuest, {
  validRoomChildAge,
} from './ResponsiveListingSearchGuest';
import ResponsiveListingSearchDate from './ResponsiveListingSearchDate';
import {
  ResponsiveSearchDrawerButton,
  ResponsiveSearchSubmitButtonWrapper,
  StyledDrawer,
} from './ResponsiveListingSearchStyle';
const BookerSearchBarDateRangeInputWrapper = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.white};
  padding: 0 15px;
  @media (max-width: ${LANDING_BREAKPOINT}) and (min-width: ${LARGE_SCREEN_BREAKPOINT}) {
    padding: 0;
  }
  @media (min-width: ${LANDING_BREAKPOINT}) {
    padding: 0;
    border-top: unset;

    .react-tiny-popover-container {
      width: 570px !important;
      top: -10px !important;
    }
  }
`;
interface ResponsiveDrawerProps {
  isShowModal: boolean;
  hideModal: () => void;
  title?: ReactNode;
  drawerHeight?: SizeUnit;
  onResearchClick?: () => void;
  shouldShallowPush?: boolean;
  detail?: boolean;
  setIsRedirecting?: React.Dispatch<React.SetStateAction<boolean>>;
  initialData?: CalendarHolidaysViewModel;
}

const ResponsiveSearch = ({
  isShowModal,
  hideModal,
  title = <CustomIcon icon={SeturLogo} width={80} height={40} />,
  drawerHeight = 'initial',
  onResearchClick,
  detail,
  setIsRedirecting,
  initialData,
}: ResponsiveDrawerProps) => {
  const params = useParams();
  const [isShowSearchListingLocation, setIsSHowSearchListingLocation] =
    useState(false);

  const [isShowSearchListingGuest, setIsSHowSearchListingGuest] =
    useState(false);

  const [isShowSearchListingDate, setIsSHowSearchListingDate] = useState(false);
  const router = useRouter();
  const {
    hotelRoomsList,
    setHotelRoomsList,
    location,
    setLocation,
    startDate,
    endDate,
    isHotelDateSelected,
  } = useSearchBarContext();

  const [locationInputError, setLocationInputError] = useState(false);

  const [hotelRooms, setHotelRooms] = useState<
    Array<ListingSearchRoomInterface>
  >([...hotelRoomsList]);

  const [inputLocation, setInputLocation] = useState<string>(
    location?.text || ''
  );
  const debounceLocation = useDebounceValue<string>(inputLocation);
  const [selectedLocation, setSelectedLocation] = useState<any>();
  const [defaultStarDate, setDefaultStarDate] = useState('');
  const [defaultEndDate, setDefaultEndDate] = useState('');
  const { push } = useRouterNext();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const pathname = usePathname();

  const inputRef = useRef<HTMLInputElement>(null);

  const clearLocation = () => {
    setInputLocation('');
    if (!detail) {
      setLocation({
        id: 0,
        text: '',
        url: '',
      });
    }
  };

  const handleUpdateFinalRooms = (
    rooms: Array<ListingSearchRoomInterface>
  ): void => {
    setHotelRoomsList(rooms);
  };

  useEffect(() => {
    if (!isHotelDateSelected) return;
    setIsSHowSearchListingDate(true);
  }, [isHotelDateSelected]);

  // ===================================
  // Search Button Clicked
  // ===================================
  const handleSearchClick = async (): Promise<void> => {
    if (!location.text) {
      setLocationInputError(true);
      return;
    }
    setIsRedirecting?.(true);
    const parsedStart = moment(defaultStarDate, 'DD.MM.YYYY', true);
    const parsedEnd = moment(defaultEndDate, 'DD.MM.YYYY', true);
    if (parsedStart.isValid()) {
      Cookies.set('startDate', parsedStart.format('YYYY-MM-DD'));
    }

    if (parsedEnd.isValid()) {
      Cookies.set('endDate', parsedEnd.format('YYYY-MM-DD'));
    }
    Cookies.set('localRooms', JSON.stringify(hotelRooms));
    const rooms = hotelRooms?.map(
      (item) =>
        `${item.adultCount}${
          item.childrenList.length > 0 ? '_' : ''
        }${item.childrenList?.map((item) =>
          item.age
            .toString()
            ?.split('/')
            ?.join('-')
            .split('-')
            .reverse()
            .join('-')
        )}`
    );
    const newQuery = clean({
      in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
      out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      room: rooms,
      slug: query.slug as string,
      sort: query.sort as string,
      page: query.page ? (query.page as string) : '1',
      size: query.size as string,
    });

    // ===================================
    // Add new search to the local storage
    // ===================================
    const items = localStorage.getItem(HOTEL_PAST_SEARCH)
      ? JSON.parse(localStorage.getItem(HOTEL_PAST_SEARCH) || '')
      : [];

    const newSearch = clean({
      in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
      out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      rooms: hotelRoomsList,
      slug: location.url,
      ...location,
    });

    if (!items.find((i: any) => i.url === location.url)) {
      items.unshift(newSearch);
    } else {
      const target = items.find((i: any) => i.url === location.url);

      const targetIndex = items.indexOf(target);
      items.splice(targetIndex, 1);
      items.unshift(newSearch);
    }

    localStorage.setItem(
      HOTEL_PAST_SEARCH,
      JSON.stringify(items, hotelItemsTOStringify)
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { slug, ...nextQuery } = newQuery;

    hideModal();
    onResearchClick?.();
    if (
      location.type === WebUrlType.HOTEL_CITY ||
      location.type === WebUrlType.HOTEL_REGION ||
      location.type === WebUrlType.HOTEL_CHAIN ||
      location.type === WebUrlType.HOTEL_THEME
    ) {
      if (location.redirectUrl) {
        const roomUrlStr = generateSearchRooms(hotelRooms);
        const href = handleFilterAndUpdateUrl(
          clean({
            in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
            out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
            slug: location?.url,
            room: roomUrlStr,
          })
        );
        router.push(`/${href}`);
      } else {
        const currentPath = params?.slug as string;

        if (currentPath !== location.url) {
          const roomUrlStr = generateSearchRooms(hotelRooms);
          const href = handleFilterAndUpdateUrl(
            clean({
              in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
              out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
              slug: location?.url,
              room: roomUrlStr,
            })
          );
          router.push(`/${href}`);
        } else {
          push({
            pathname: `/${location?.url}`,
            query: nextQuery,
          });
        }
        clearLocation();
      }
    } else if (location.type === WebUrlType.HOTEL) {
      if (location.redirectUrl) {
        const roomUrlStr = generateSearchRooms(hotelRooms);
        const href = handleFilterAndUpdateUrl(
          clean({
            in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
            out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
            slug: location?.url,
            room: roomUrlStr,
          })
        );
        router.push(`/${href}`);
      } else {
        const currentPath = params?.slug as string;
        if (currentPath !== location.url) {
          const roomUrlStr = generateSearchRooms(hotelRooms);
          const href = handleFilterAndUpdateUrl(
            clean({
              in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
              out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
              slug: location?.url,
              room: roomUrlStr,
            })
          );
          router.push(`/${href}`);
        } else {
          push({
            pathname: `/${location.url}`,
            query: {
              in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
              out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
              room: rooms,
            },
          });
        }

        clearLocation();
      }
    } else {
      push({
        pathname: '/',
        query: nextQuery,
      });
      clearLocation();
    }
  };

  // ================================================
  // Fetch locations/hotels and fill the search by location
  // ================================================
  const fetchLocationsItems = async () => {
    return callService(
      { service: 'SearchesService', method: 'searchesHotel', version: 'v3' },
      {
        s: debounceLocation?.toLocaleLowerCase(),
      }
    );
  };

  const {
    data: locations = [],
    isError,
    isInitialLoading: isLoading,
  } = useQuery(['locationItems', debounceLocation], fetchLocationsItems, {
    enabled: Boolean(
      debounceLocation.length >= SEARCH_LENGTH && inputRef.current
    ),
  });

  // ==============================================
  // Handling the input search
  // ==============================================
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputLocation(e.target.value);
  };

  // ==============================================
  // Handling selecting an item from the autocomplete list
  // ==============================================
  const handleLocationClicked = (i: any): void => {
    setSelectedLocation(i);
    setInputLocation(i.text);
  };

  const [openAutoCompleteResult, setOpenAutoCompleteResult] =
    useState<boolean>(false);
  // ==============================================
  // Handling Clicking on the apply button
  // ==============================================
  const applySearchLocationHandler = () => {
    setLocation(selectedLocation);
    setIsSHowSearchListingLocation(false);
  };

  // ==============================================
  // Handling Clicking on close location search icon
  // ==============================================

  const handleCloseLocationSearch = (): void => {
    if (location?.text) {
      setInputLocation(location?.text);
    }
    setIsSHowSearchListingLocation(false);
    setLocation(location);
    setOpenAutoCompleteResult(false);
  };

  const clearRoomsValues = (): void => {
    setHotelRooms(hotelRoomsList);
  };

  // ============================================================
  // -Custom functions to control the children error
  // -using this function we can find an invalid child age and update te error property
  // =============================================================
  const handleChildError = (): void => {
    setHotelRooms((hr) =>
      hr?.map((room) =>
        room?.childrenList?.length !== 0
          ? {
              ...room,
              childrenList:
                room.childrenList &&
                room.childrenList?.map((kid: KidInterface) =>
                  validRoomChildAge(String(kid.age))
                    ? { ...kid, error: false }
                    : { ...kid, error: true }
                ),
            }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control the rooms
  // -using this function we can add new room
  // =============================================================
  const addRoom = (): void => {
    setHotelRooms([
      ...hotelRooms,
      {
        id: generateUUID(),
        adultCount: 2,
        childrenList: [],
        title: `${hotelRooms.length + 1}. Oda`,
        closable: true,
        error: false,
        key: String(hotelRooms[hotelRooms.length - 1].key + 1),
        ref: React.createRef<HTMLDivElement>(),
      },
    ]);
  };

  // ============================================================
  // -Custom functions to handle the close mobile guest modal
  // =============================================================
  const handleHideMobileGuestModal = () => {
    if (validateGuestsChildrenAges(hotelRooms)) {
      handleChildError();
    } else {
      setIsSHowSearchListingGuest(false);
    }
  };

  // ============================================================
  // -Custom functions to handle the show mobile guest modal
  // =============================================================
  const handleShowMobileGuestModal = () => {
    clearRoomsValues();
    setIsSHowSearchListingGuest(true);
  };

  useLayoutEffect(() => {
    if (
      pathname === '/seyahat-planlama' ||
      pathname === '/profil/favorilerim'
    ) {
      clearLocation();
    }
  }, []);

  const searchAgainButtonDisabled = useMemo(() => {
    if (startDate && endDate) {
      return false;
    }
    if (!startDate && !endDate) {
      return false;
    }
    if (startDate && !endDate) {
      return true;
    }
    if (!startDate && endDate) {
      return true;
    }
  }, [startDate, endDate]);

  return (
    <BookerSearchBarDateRangeInputWrapper>
      <StyledDrawer
        title={title}
        closeIcon={
          <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='black' />
        }
        placement='top'
        onClose={() => {
          clearLocation();
          hideModal();
        }}
        visible={isShowModal}
        height={drawerHeight}
        footer={
          <ResponsiveSearchSubmitButtonWrapper>
            <ResponsiveSearchDrawerButton
              variant='Primary'
              block
              onClick={handleSearchClick}
              disabled={searchAgainButtonDisabled}
            >
              Yeniden Ara
            </ResponsiveSearchDrawerButton>
          </ResponsiveSearchSubmitButtonWrapper>
        }
      >
        <div>
          <LocationAnimationErrorBox hasError={locationInputError}>
            <MobileSearchLocation
              showLocationModal={() => setIsSHowSearchListingLocation(true)}
              width='100%'
              height='100%'
              placement='top'
              title={
                pathname === '/seyahat-planlama'
                  ? 'Otel / Konum Arama'
                  : 'Yurt İçi - Yurt Dışı, Otel Adı veya Konum'
              }
              visible={isShowSearchListingLocation}
              locationInput={inputLocation}
              handleLocationInputChange={handleChange}
              autoCompleteData={locations}
              autoCompleteItemClicked={handleLocationClicked}
              locationInputError={locationInputError}
              handleUpdateButton={applySearchLocationHandler}
              isFilled={inputLocation.length > 0}
              inputRef={inputRef}
              searchTextError={isError}
              searchTextLoading={isLoading}
              handleCloseLocationSearch={handleCloseLocationSearch}
              openAutoCompleteResultModal={openAutoCompleteResult}
              setOpenAutoCompleteResultModal={setOpenAutoCompleteResult}
              locationDrawerSubTitle={location?.text || 'Otel adı veya konum'}
              type={inputLocation ? undefined : 'hotel'}
            />
          </LocationAnimationErrorBox>

          <ResponsiveListingSearchDate
            showDateModal={() => setIsSHowSearchListingDate(true)}
            initialData={initialData}
            width='100%'
            height='100%'
            title='Tarih Arama'
            placement='top'
            setDefaultEndDate={setDefaultEndDate}
            setDefaultStarDate={setDefaultStarDate}
            visible={isShowSearchListingDate}
            hideDateModal={() => {
              setIsSHowSearchListingDate(false);
            }}
          />

          <ResponsiveListingSearchGuest
            width='100%'
            height='100%'
            placement='top'
            visible={isShowSearchListingGuest}
            showGuestModal={handleShowMobileGuestModal}
            hideGuestModal={handleHideMobileGuestModal}
            rooms={hotelRooms}
            onHotelRoomsUpdate={(hr) => setHotelRooms(hr)}
            finialRooms={hotelRoomsList}
            handleUpdateFinalRooms={handleUpdateFinalRooms}
            handleChildError={handleChildError}
            addNewRoom={addRoom}
            searchText='Kişi, Oda Sayısı'
          />
        </div>
      </StyledDrawer>
    </BookerSearchBarDateRangeInputWrapper>
  );
};

export default ResponsiveSearch;
